import { Signal, signal } from "@preact/signals";
import { requestApi } from "~/core/api";
import { Chat, Message } from "~/models";
import {
    ListChatsResponse,
    ListMessagesResponse,
    SendMessageParams,
} from "./interfaces";

// @TODO: socket event - last message changed

const makeChats = () => {
    const chats = signal<Chat[]>([]);
    const messagesStore: Record<string, Signal<Message[]>> = {};
    const loadingMessages: Record<string, Signal<boolean>> = {};
    const loading = signal(false);
    const popup = signal(true);
    const popupMinimised = signal(false);

    const fetchChats = async () => {
        try {
            loading.value = true;

            const response = await requestApi<ListChatsResponse>("/chats");

            if (response.ok) {
                chats.value = response.data.chats;
            }
        } catch (error: unknown) {
            console.error(error);
        } finally {
            loading.value = false;
        }
    };

    const fetchMessages = async (threadId: string) => {
        try {
            loadingMessages[threadId].value = true;

            const response = await requestApi<ListMessagesResponse>(
                `/chats/${threadId}/messages`
            );

            if (response.ok) {
                messagesStore[threadId].value = response.data.messages;
            }
        } catch (error: unknown) {
            console.error(error);
        } finally {
            loadingMessages[threadId].value = false;
        }
    };

    const load = () => {
        if (loading.value || chats.value.length > 0) {
            return;
        }

        fetchChats();
    };

    const reload = () => {
        if (loading.value) {
            return;
        }

        fetchChats();
    };

    const loadMessages = (threadId: string) => {
        if (
            loadingMessages[threadId].value ||
            messagesStore[threadId].value.length > 0
        ) {
            return;
        }

        fetchMessages(threadId);
    };

    const reloadMessages = (threadId: string) => {
        if (loading.value) {
            return;
        }

        fetchMessages(threadId);
    };

    const messages = (threadId: string) => {
        if (!messagesStore[threadId]) {
            messagesStore[threadId] = signal<Message[]>([]);
            loadingMessages[threadId] = signal(false);
            loadMessages(threadId);
        }

        return messagesStore[threadId];
    };

    const isMessagesLoading = (threadId: string) => {
        if (!loadingMessages[threadId]) {
            loadingMessages[threadId] = signal(false);
        }

        return loadingMessages[threadId];
    };

    const send = async (params: SendMessageParams) => {
        try {
            const response = await requestApi(
                `/chats/${params.threadId}/messages`,
                "POST",
                {
                    content: params.content,
                }
            );

            if (response.ok) {
                reloadMessages(params.threadId);
            }
        } catch (error: unknown) {
            console.error(error);
        }
    };

    return {
        chats,
        loading,
        popup,
        popupMinimised,
        send,
        load,
        reload,
        loadMessages,
        reloadMessages,
        messages,
        isMessagesLoading,
    };
};

export default makeChats;
